/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useTranslation } from 'react-i18next';
import { BuildingIcon } from '@/shared/ui/components/basic/icons';
import { Text } from '@/shared/ui';
import { formatCurrency } from '../PropertyFormDualPane/utils';
import { getCategoryLabel } from './schema';
import type { PropertyFormValues } from './schema';

interface PropertyPreviewBarProps {
  values: Partial<PropertyFormValues>;
}

/**
 * Compact live-preview strip shown above the property drawer form. Displays the
 * title (or category fallback), category, bedroom/parking badges, and the sale
 * or rental price as the user fills the form. Renders nothing until at least
 * one summarizable field has a value.
 */
export function PropertyPreviewBar({ values }: PropertyPreviewBarProps) {
  const { t } = useTranslation('features/properties');
  const { category, property_title, sale_price, rental_price, bedrooms, parking_spots } = values;

  const hasContent = category || property_title || sale_price || rental_price;
  if (!hasContent) return null;

  const priceDisplay = sale_price
    ? formatCurrency(sale_price)
    : rental_price
      ? t('propertyDrawer.previewBar.perMonth', { value: formatCurrency(rental_price) })
      : null;

  const badges: string[] = [];
  if (bedrooms && bedrooms > 0) badges.push(t('propertyDrawer.previewBar.bedroomsShort', { count: bedrooms }));
  if (parking_spots && parking_spots > 0) badges.push(t('propertyDrawer.previewBar.parkingShort', { count: parking_spots }));

  return (
    <div style={{
      padding: 'var(--space-3) var(--space-4)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-subtle)',
      background: 'var(--surface-secondary)',
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-3)',
      fontFamily: 'var(--font-family)',
    }}>
      <BuildingIcon width={16} height={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {property_title || (category ? getCategoryLabel(category) : t('propertyDrawer.previewBar.newProperty'))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
          {category && (
            <Text as="span" style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
              {getCategoryLabel(category)}
            </Text>
          )}
          {badges.map(b => (
            <Text as="span" key={b} style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>· {b}</Text>
          ))}
        </div>
      </div>
      {priceDisplay && (
        <Text as="span" style={{ fontSize: 13, fontWeight: 700, color: 'var(--seed-base)', whiteSpace: 'nowrap' }}>
          {priceDisplay}
        </Text>
      )}
    </div>
  );
}
